import * as z from "zod";
import { type Caller, cursorPageOf, cursorPagination, type Result } from "../index";
import { externalSystems } from "../til";

// The reconciliation of Nexxt with Business Central (FR-M16-03, ADR-177): what each domain finds wrong in its own data
// about what Business Central should hold, asked every hour by worklist and kept by integration in
// `platform.reconciliation_issue`. Comparing with what Business Central really holds waits for TIL (T-54). Internal
// users only.

/**
 * What is wrong with an entity:
 * - `sync_complete_without_bc_id`: the system took the state, and the id it should have given is not in
 *   `platform.external_ref`;
 * - `outcome_not_applied`: the outcome of a message reached integration a while ago, and the entity still waits for it;
 * - `never_sent`: the entity is in a state Business Central must see, and no message about it ever left.
 */
export const reconciliationKinds = ["sync_complete_without_bc_id", "outcome_not_applied", "never_sent"] as const;
export type ReconciliationKind = (typeof reconciliationKinds)[number];

/** Only the hourly job holds it: no role gives it, so no user calls what the domains answer, or records it. */
export const BC_SYNC_RECONCILE = "bc_sync.reconcile";

/** At most this many findings of a kind from a domain: one beyond says the list was cut. */
export const MAX_RECONCILIATION_FINDINGS = 500;

/**
 * An entity a domain finds wrong. `entityType` is the entity whose page shows its state towards Business Central, where
 * its domain sends it again (ADR-164): a linesheet for its SKUs and its sync runs. `details` holds ids and facts, and
 * the number or the name users know the entity by (`reference`), as they were when the check last found it.
 */
export const reconciliationFinding = z.object({
  entityType: z.string().min(1).max(80),
  entityId: z.uuid(),
  kind: z.enum(reconciliationKinds),
  details: z.record(z.string(), z.union([z.string(), z.number(), z.null()])),
});
export type ReconciliationFinding = z.output<typeof reconciliationFinding>;

/** What a domain finds: `cut` names the kinds of entity whose list stopped at `MAX_RECONCILIATION_FINDINGS`. */
export const reconciliationFindingsDto = z.object({
  findings: z.array(reconciliationFinding),
  cut: z.array(z.object({ entityType: z.string(), kind: z.enum(reconciliationKinds) })),
});
export type ReconciliationFindings = z.output<typeof reconciliationFindingsDto>;

export const listBcReconciliationFindingsInput = z.object({});

/** The hourly check of worklist takes nothing: it asks every domain. */
export const checkBcReconciliationInput = z.object({});
export type ListBcReconciliationFindingsInput = z.input<typeof listBcReconciliationFindingsInput>;

/** The domains with messages to Business Central: the schema each entity type begins with (ADR-177). */
export const reconciledDomains = ["catalog", "procurement", "sales", "logistics", "party", "documents"] as const;
export type ReconciledDomain = (typeof reconciledDomains)[number];

/** The method every domain with messages to Business Central serves to the hourly job of worklist. */
export interface BcReconciliationSource {
  listBcReconciliationFindings(
    caller: Caller,
    input: ListBcReconciliationFindingsInput,
  ): Promise<Result<ReconciliationFindings>>;
}

/**
 * What the domains answered, for integration to open and close the issues: a domain that did not answer is absent,
 * and keeps its issues. A domain names only entities of its own schema.
 */
export const recordBcReconciliationInput = z.object({
  answers: z
    .partialRecord(z.enum(reconciledDomains), reconciliationFindingsDto)
    .refine(
      (answers) =>
        Object.entries(answers).every(([domain, found]) =>
          [...(found?.findings ?? []), ...(found?.cut ?? [])].every((f) => f.entityType.startsWith(`${domain}.`)),
        ),
      "a domain names only entities of its own schema",
    ),
});
export type RecordBcReconciliationInput = z.input<typeof recordBcReconciliationInput>;

export const bcReconciliationRecordedDto = z.object({ opened: z.number().int(), resolved: z.number().int() });
export type BcReconciliationRecorded = z.output<typeof bcReconciliationRecordedDto>;

export const reconciliationStates = ["open", "resolved"] as const;

/** The issues, the latest found first. */
export const listReconciliationIssuesInput = z.object({
  kind: z.enum(reconciliationKinds).optional(),
  /** e.g. `procurement.purchase_order`: exact. */
  entityType: z.string().trim().min(1).max(80).optional(),
  state: z.enum(reconciliationStates).default("open"),
  ...cursorPagination({ defaultLimit: 50, maxLimit: 200 }),
});
export type ListReconciliationIssuesInput = z.input<typeof listReconciliationIssuesInput>;

export const reconciliationIssueDto = z
  .object({
    id: z.uuid(),
    system: z.enum(externalSystems),
    entityType: z.string(),
    entityId: z.uuid(),
    kind: z.enum(reconciliationKinds),
    details: z.record(z.string(), z.union([z.string(), z.number(), z.null()])),
    detectedAt: z.iso.datetime(),
    /** When a later check no longer found it. */
    resolvedAt: z.iso.datetime().nullable(),
  })
  .meta({ id: "ReconciliationIssue" });
export type ReconciliationIssue = z.output<typeof reconciliationIssueDto>;

export const reconciliationIssuePageDto = cursorPageOf(reconciliationIssueDto).meta({ id: "ReconciliationIssuePage" });

export const getReconciliationSummaryInput = z.object({});
export type GetReconciliationSummaryInput = z.input<typeof getReconciliationSummaryInput>;

/** The open issues, by kind. */
export const reconciliationSummaryDto = z
  .object({
    syncCompleteWithoutBcId: z.number().int(),
    outcomeNotApplied: z.number().int(),
    neverSent: z.number().int(),
  })
  .meta({ id: "ReconciliationSummary" });
export type ReconciliationSummary = z.output<typeof reconciliationSummaryDto>;
